import {
  ScanSearch,
  ShieldCheck,
  Droplets,
  Disc3,
  CircleDot,
  Cog,
  Crosshair,
  BatteryCharging,
  Wrench,
  ThermometerSnowflake,
  ClipboardCheck,
  Power,
  CalendarCheck2,
  Snowflake,
  Car,
} from "lucide-react";

export const BUSINESS = {
  name: "Auto Trust Centre",
  shortName: "Auto Trust",
  phone: "(905) 206-1276",
  phoneHref: "tel:+19052061276",
  established: 2007,
  addressLines: ["5040 Maingate Dr #1", "Mississauga, ON L4W 1X5", "Canada"],
  addressOneLine: "5040 Maingate Dr #1, Mississauga, ON L4W 1X5",
};

export const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "About", id: "about" },
  { label: "Reviews", id: "reviews" },
  { label: "Contact", id: "contact" },
];

const mapsQuery = encodeURIComponent(
  "Auto Trust Centre, 5040 Maingate Dr #1, Mississauga, ON L4W 1X5"
);
export const MAPS_EMBED_URL = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;
export const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`;
export const GOOGLE_REVIEWS_URL = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

export const BUSINESS_HOURS = [
  { day: "Monday", open: "8:30 AM", close: "5:00 PM" },
  { day: "Tuesday", open: "8:30 AM", close: "5:00 PM" },
  { day: "Wednesday", open: "8:30 AM", close: "5:00 PM" },
  { day: "Thursday", open: "8:30 AM", close: "5:00 PM" },
  { day: "Friday", open: "8:30 AM", close: "5:00 PM" },
  { day: "Saturday", open: "8:30 AM", close: "2:00 PM" },
  { day: "Sunday", closed: true },
];

export const SERVICES = [
  {
    slug: "diagnostics",
    icon: ScanSearch,
    title: "Diagnostics & Check Engine Light",
    blurb: "Warning light on? We find the real cause before recommending any repair.",
    detail:
      "Professional diagnosis of check engine lights, warning indicators, drivability concerns and vehicle problems. We test first, explain what we find, and only then recommend the repair your vehicle actually needs.",
  },
  {
    slug: "safety-certificate",
    icon: ShieldCheck,
    title: "Ontario Safety Standards Certificate",
    blurb: "Official Ontario vehicle safety inspections using the digital safety inspection system.",
    detail:
      "Auto Trust Centre provides Ontario Safety Standards Certificate inspections through Ontario's digital safety inspection system. Your vehicle is inspected against provincial safety standards and the results are recorded digitally. Book an inspection when buying, selling or transferring a vehicle, or whenever a Safety Standards Certificate is required.",
    badge: "Official Ontario Digital Inspection",
  },
  {
    slug: "oil-maintenance",
    icon: Droplets,
    title: "Oil Changes & Maintenance",
    blurb: "Routine oil changes, filters and fluids — includes a free 30-point visual inspection.",
    detail:
      "Regular oil changes, filter replacements, fluid checks and preventative maintenance that keep your engine protected and help you avoid larger repairs down the road. Every oil change includes a free 30-point visual inspection.",
  },
  {
    slug: "brakes",
    icon: Disc3,
    title: "Brake Service",
    blurb: "Inspections, pads, rotors and complete brake system repairs.",
    detail:
      "Thorough brake inspections plus pad, rotor and related brake system repairs. If your brakes squeal, pulse or feel soft, we'll measure and inspect before quoting anything.",
  },
  {
    slug: "tires",
    icon: CircleDot,
    title: "Tire Service",
    blurb: "Seasonal tire changes, mounting, balancing and inspections.",
    detail:
      "Seasonal tire changeovers, mounting, balancing and tire inspections. We check tread depth, wear patterns and pressure so your tires last longer and your vehicle drives straight.",
  },
  {
    slug: "alignment",
    icon: Crosshair,
    title: "Wheel Alignment",
    blurb: "Alignment checks and adjustment for better handling and tire wear.",
    detail:
      "Alignment inspection and adjustment to correct pulling, crooked steering wheels and uneven tire wear — improving handling and helping your tires go the distance.",
  },
  {
    slug: "steering-suspension",
    icon: Cog,
    title: "Steering & Suspension",
    blurb: "Control arms, ball joints, tie rods, wheel bearings and more.",
    detail:
      "Inspection and repair of steering and suspension components including control arms, ball joints, tie rods, wheel bearings and shocks — the parts that keep your ride stable and safe.",
  },
  {
    slug: "engine-mechanical",
    icon: Wrench,
    title: "Engine Repair, Replacement & Rebuilding",
    blurb: "From engine diagnosis to internal repairs, replacement and rebuilding.",
    detail:
      "Comprehensive engine work including engine diagnosis, internal engine repairs, engine replacement and engine rebuilding, timing-related repairs, oil and coolant leak diagnosis and repair, overheating diagnosis and other major mechanical engine repairs.",
  },
  {
    slug: "ac-climate",
    icon: Snowflake,
    title: "Automotive A/C & Climate Control",
    blurb: "A/C and heating diagnosis, service and repair.",
    detail:
      "A/C system diagnosis, A/C performance concerns, refrigerant and leak-related diagnosis, compressor and related component repair or replacement, and heating and climate-control diagnosis — keeping your vehicle comfortable in every season.",
  },
  {
    slug: "complete-mechanical",
    icon: Car,
    title: "Complete Mechanical Repairs",
    blurb: "Full-service mechanical repair under one roof.",
    detail:
      "Auto Trust Centre is a full-service mechanical repair facility: engine repair, replacement and rebuilding, transmission and drivetrain-related mechanical repairs, steering and suspension, wheel bearings, cooling systems, exhaust system repairs, brakes, A/C and heating, starting and charging systems, electrical and diagnostic work, fluid services, preventative maintenance, tires and wheel alignment, no-start and check-engine-light diagnosis, Ontario Safety Standards inspections and other general automotive mechanical repairs.",
  },
  {
    slug: "cooling",
    icon: ThermometerSnowflake,
    title: "Cooling System",
    blurb: "Coolant leaks, overheating and cooling-system repairs.",
    detail:
      "Cooling-system inspection and repair, including coolant leaks, overheating concerns, hoses, radiators and water pumps — critical protection for your engine.",
  },
  {
    slug: "battery-starting",
    icon: BatteryCharging,
    title: "Battery & Starting System",
    blurb: "Battery testing, replacement and charging system diagnosis.",
    detail:
      "Battery testing and replacement, plus diagnosis of starting and charging system concerns — alternators, starters and connections — so you're not left stranded.",
  },
  {
    slug: "inspection",
    icon: ClipboardCheck,
    title: "Vehicle Inspection",
    blurb: "General inspections and preventative safety checks.",
    detail:
      "General vehicle inspections and preventative safety checks that catch small problems early — before a road trip, a purchase, or a change of season.",
  },
  {
    slug: "no-start",
    icon: Power,
    title: "No-Start Diagnosis",
    blurb: "Cranks but won't start? We track down the cause.",
    detail:
      "Systematic diagnosis of vehicles that crank but won't start, or that have intermittent starting problems. We isolate the cause — fuel, spark or electrical — instead of guessing at parts.",
  },
  {
    slug: "scheduled-maintenance",
    icon: CalendarCheck2,
    title: "Scheduled Maintenance",
    blurb: "Manufacturer-recommended and preventative maintenance.",
    detail:
      "Manufacturer-recommended and preventative maintenance services that follow your vehicle's schedule, protect its longevity and keep service records up to date.",
  },
];

export const FEATURED_SERVICES = [
  "diagnostics",
  "safety-certificate",
  "oil-maintenance",
  "brakes",
  "tires",
  "steering-suspension",
  "engine-mechanical",
  "ac-climate",
  "complete-mechanical",
];

export const WHY_US = [
  {
    title: "Accurate Diagnosis",
    text: "Proper repair begins by determining the actual cause of the problem. We test and verify before we recommend, so you never pay for guesswork.",
  },
  {
    title: "Straightforward Recommendations",
    text: "You should understand what your vehicle needs and why. We explain our findings in plain language and lay out your options honestly.",
  },

  {
    title: "Quality Repairs",
    text: "Correct installation and durable repair solutions matter. We do the job properly the first time and stand behind our workmanship.",
  },
  {
    title: "Experienced Team",
    text: "Our technicians bring years of hands-on automotive experience and keep pace with changing vehicle technology through continued knowledge and training.",
  },
  {
    title: "Respect for Your Vehicle & Budget",
    text: "Every repair is approached with consideration for your vehicle, your time and your money. No unnecessary repairs. No aggressive upselling.",
  },
];

// Insert real Google reviews here when available — the Reviews section renders them automatically.
// Shape: { name, date, rating (1-5), text }
export const REVIEWS = [];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};
